import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { SidebarTabLayout } from '../shared/SidebarTabLayout';

import { useDispatch, useSelector } from 'react-redux';
import { recordingsModel } from '../../../../recordings';
import { authUserModel } from '../../../../../entities/auth-user';
import { SortType } from '../../../../recordings/config';
import { RecordCard } from './RecordCard';
import { RecordCategory } from '../../../../../entities/recordings';
import { getSortResults } from '../../../../recordings/lib';
import { RecordsHeader } from './RecordHeader';
import { NoRecordsFoundScreen } from './NoRecordsFoundScreen';
import { VideoRecordType } from '@distribute/shared/api-types/video-record';
import { RecordPreview } from './RecordPreview';
import { editorLeftSidebarModel } from '../../../model';
import { useTiptapEditor } from '../../../../../entities/tiptap-editor';
import { DEFAULT_RATIO } from '@distribute/shared/utils';

export const RecordsTab = () => {
  const dispatch = useDispatch();

  const { editor } = useTiptapEditor();
  const currentUser = useSelector(authUserModel.selectors.selectUserWithError);

  const [selectedTab, setSelectedTab] = useState(RecordCategory.MY);
  const [sortMethod, setSortMethod] = useState<SortType>(SortType.LAST_CREATED);

  const [isSearchInputOpen, setIsSearchInputOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [recordingPreview, setRecordingPreview] =
    useState<VideoRecordType | null>(null);

  const myRecords = useSelector(
    recordingsModel.selectors.selectRecordsMy(currentUser.id)
  );

  const sharedRecords = useSelector(
    recordingsModel.selectors.selectRecordsShared(currentUser.id)
  );

  const allRecords = useSelector(recordingsModel.selectors.selectRecords);

  const visibleRecords = useMemo(() => {
    if (searchQuery) {
      return allRecords.filter((r) =>
        new RegExp(searchQuery, 'ig').test(r.name)
      );
    }

    return selectedTab === RecordCategory.MY
      ? getSortResults(myRecords, sortMethod)
      : getSortResults(sharedRecords, sortMethod);
  }, [
    allRecords,
    myRecords,
    sharedRecords,
    searchQuery,
    selectedTab,
    sortMethod,
  ]);

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleInsert = (record: VideoRecordType) => {
    dispatch(editorLeftSidebarModel.actions.setSelectedOption(null));
    editor
      ?.chain()
      .setVideoRecord({
        prefix: record.prefix,
        playbackId: record.playbackId || '',
        aspectRatio: record.metadata?.mux?.aspectRatio || DEFAULT_RATIO,
        name: record.name,
      })
      .run();
  };

  const clearSearchQuery = () => {
    setSearchQuery('');
  };

  const closePreview = () => {
    setRecordingPreview(null);
  };

  return (
    <div className="w-153">
      {recordingPreview ? (
        <RecordPreview
          record={recordingPreview}
          closePreview={closePreview}
          onInsert={handleInsert}
        />
      ) : (
        <SidebarTabLayout
          headerComponent={
            <RecordsHeader
              selectedTab={selectedTab}
              setSelectedTab={
                setSelectedTab as Dispatch<SetStateAction<string>>
              }
              isSearchInputOpen={isSearchInputOpen}
              setIsSearchInputOpen={setIsSearchInputOpen}
              searchQuery={searchQuery}
              handleSearchQueryChange={handleSearchQueryChange}
              sortMethod={sortMethod}
              setSortMethod={setSortMethod}
              clearSearch={clearSearchQuery}
            />
          }
        >
          {visibleRecords.length > 0 ? (
            <div className="grid flex-grow grid-cols-2 gap-3 overflow-x-hidden overflow-y-auto place-content-start">
              {visibleRecords.map((record) => (
                <RecordCard
                  record={record}
                  setRecordingPreview={setRecordingPreview}
                  category={selectedTab}
                  onInsert={handleInsert}
                />
              ))}
            </div>
          ) : (
            <NoRecordsFoundScreen
              clearSearchQuery={clearSearchQuery}
              isClearSearchButton={Boolean(searchQuery)}
            />
          )}
        </SidebarTabLayout>
      )}
    </div>
  );
};
